import React, { useState } from 'react';
import { generateZenStory } from '../services/geminiService';
import { ZenStory } from '../types';
import { BookOpen, Moon, ArrowLeft, Feather, Share2 } from 'lucide-react';
import { useUser } from '../context/UserContext';

// 52 Themes for the year
const THEMES = [
  "Empty Cup", "Beginner's Mind", "Impermanence", "Non-Attachment", "The Heavy Load", 
  "Silence", "The Present Moment", "Flow Like Water", "The Ego", "Compassion",
  "Patience", "Judgement", "True Nature", "Simplicity", "Action through Inaction",
  "The Mirror", "Gratitude", "Suffering", "Desire", "Focus",
  "The Unknown", "Flexibility", "Balance", "Integrity", "Kindness",
  "Letting Go", "Wisdom", "Anger", "Joy", "Reflection",
  "Discipline", "Service", "Humility", "Courage", "Trust",
  "Harmony", "Observation", "Stillness", "Awareness", "Acceptance",
  "Generosity", "Resilience", "Authenticity", "Connection", "Solitude",
  "Purpose", "Growth", "Forgiveness", "Truth", "Love", "Peace", "Completion"
];

const ZenMonkey: React.FC = () => {
  const [selectedTheme, setSelectedTheme] = useState<string | null>(null);
  const [story, setStory] = useState<ZenStory | null>(null);
  const [loading, setLoading] = useState(false);
  const { addXp, unlockBadge } = useUser();

  const handleSelectTheme = async (theme: string) => {
    setSelectedTheme(theme);
    setLoading(true);
    setStory(null);
    try {
      const data = await generateZenStory(theme);
      setStory(data);
      addXp(10, `Read Zen Story: ${theme}`);
      unlockBadge('zen_mind');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const backToGrid = () => {
    setSelectedTheme(null);
    setStory(null);
  };

  const handleShare = async () => {
    if (!story) return;
    const text = `${story.title}\n\n${story.content}\n\nLesson: ${story.lesson}\n\nShared via Nunchi Master`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: story.title,
          text: text,
        });
      } catch (err) {
        console.error("Share failed", err);
      }
    } else {
      navigator.clipboard.writeText(text);
      alert("Story copied to clipboard!");
    }
  };

  if (selectedTheme) {
    return (
      <div className="max-w-2xl mx-auto px-6 pb-20 animate-fadeIn">
        <button 
          onClick={backToGrid}
          className="flex items-center gap-2 text-nunchi-500 hover:text-nunchi-800 transition-colors mb-6 font-medium text-sm"
        >
          <ArrowLeft size={16} /> Back to Library
        </button>

        <div className="bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-nunchi-200 relative overflow-hidden">
          {/* Decorative background element */}
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-amber-50 rounded-full opacity-50 blur-3xl"></div>
          
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-12 h-12 border-4 border-amber-100 border-t-amber-500 rounded-full animate-spin mb-4"></div>
              <p className="font-serif italic text-nunchi-500">The ink is drying on the parchment...</p>
            </div>
          ) : story ? (
            <div className="relative z-10">
               <div className="flex justify-center mb-6 text-amber-500">
                 <Moon size={32} />
               </div>
               <h1 className="text-3xl font-serif font-bold text-center text-nunchi-900 mb-2">{story.title}</h1>
               <div className="w-16 h-1 bg-amber-200 mx-auto mb-8"></div>
               
               <div className="prose prose-lg text-nunchi-700 leading-relaxed font-serif mb-8 first-letter:text-4xl first-letter:font-bold first-letter:mr-1 first-letter:float-left first-letter:text-amber-600">
                 {story.content}
               </div>

               <div className="bg-nunchi-50 p-6 rounded-xl border-l-4 border-amber-400 mb-8">
                 <h3 className="text-sm font-bold text-nunchi-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                   <Feather size={14} /> The Lesson
                 </h3>
                 <p className="text-nunchi-800 font-medium italic">
                   "{story.lesson}"
                 </p>
               </div>

               <div className="flex justify-center">
                 <button 
                   onClick={handleShare}
                   className="flex items-center gap-2 px-6 py-2 bg-white border border-amber-200 text-amber-700 rounded-full hover:bg-amber-50 transition-colors font-bold text-sm shadow-sm"
                 >
                   <Share2 size={16} /> Share Wisdom
                 </button>
               </div>
            </div>
          ) : (
             <div className="text-center py-20 text-red-400">
               <p>The scroll is blank. Please try again.</p>
               <button onClick={() => handleSelectTheme(selectedTheme)} className="mt-4 underline">Retry</button>
             </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-amber-50 text-amber-600 rounded-full mb-4">
          <BookOpen size={32} />
        </div>
        <h1 className="text-4xl font-serif font-bold text-nunchi-900 mb-3">The Zen Monkey & The Lotus Flower</h1>
        <p className="text-xl text-nunchi-500 italic font-serif mb-6">"52 Stories to Cultivate Stillness"</p>
        <p className="text-nunchi-600 max-w-xl mx-auto leading-relaxed">
          While Nunchi masters the room and Ikigai masters the self, 
          Zen masters the moment. Choose a week to reveal its wisdom.
        </p>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {THEMES.map((theme, index) => (
          <button
            key={index}
            onClick={() => handleSelectTheme(theme)}
            className="group relative bg-white border border-nunchi-200 rounded-xl p-4 hover:shadow-md hover:border-amber-300 transition-all text-left h-32 flex flex-col justify-between overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-white via-white to-amber-50 rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
            
            <div className="text-xs font-bold text-nunchi-400 uppercase tracking-wider mb-2">
              Week {index + 1}
            </div>
            <div className="font-serif font-bold text-nunchi-800 text-lg group-hover:text-amber-700 transition-colors z-10">
              {theme}
            </div>
            <div className="mt-2 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 text-amber-500">
               <Moon size={16} />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ZenMonkey;